import { PAGE_EDITOR_IDS } from '@/components/admin/experiences/page-editor/contracts';
import {
  CONDITION_NOYAUX,
  type SimulatedVariable,
  type Simulation,
  noyauOptionLabel,
  simulatedValue,
} from '@/lib/display-conditions';
import { cn } from '@/lib/utils';
import type { VariableSources } from '@/lib/variable-pill';
import { pillOf, variableStandingOf } from '@/lib/variable-pill';
import type { ExperienceVariable } from '@matter_extractor/shared';
import { assertNever } from '@matter_extractor/shared';
import { Eye } from 'lucide-react';
import { useId } from 'react';

/**
 * La barre « Simuler » au-dessus de l'aperçu (refonte modulaire §9.2, H73 ;
 * maquette `EditeurConditions` ; issue #150).
 *
 * « L'aperçu montre par défaut tous les blocs, les conditionnels marqués, et une
 * simulation fixe les valeurs que les conditions de la page citent pour voir la
 * page telle qu'une personne la verra. » So the bar carries ONE control per
 * variable the page's conditions cite - a select of the six noyaux, a select of
 * a page's Boutons, a box for a Checkbox, « rempli » for an Input, « réussi »
 * for an objective - and the switch « Montrer tous les blocs », on by default.
 *
 * Setting a value turns the switch OFF: a value set under « montrer tous les
 * blocs » changes nothing on screen, and a control that answers nothing reads
 * as broken. The switch stays the operator's to turn back on.
 *
 * The noyau lists the six, whatever the pair of « Aperçu avec » (#151): the
 * simulation is precisely how an operator composes a result page for a noyau
 * nobody has determined yet, and the portrait of the preview follows the one
 * chosen here. « Aucun » is the skipped sequence (H71): every block conditioned
 * by the noyau masked.
 */

export interface SimulationBarProps {
  readonly variables: readonly SimulatedVariable[];
  readonly simulation: Simulation;
  readonly showAll: boolean;
  /** What names each variable in its control: the pill of the zone (§8.6, D5). */
  readonly sources: VariableSources;
  readonly onSimulation: (next: Simulation) => void;
  readonly onShowAll: (next: boolean) => void;
}

/** The variable of a simulated one, for the pill that names it. */
function variableOf(variable: SimulatedVariable): ExperienceVariable {
  switch (variable.kind) {
    case 'noyau':
      return { kind: 'noyau', id: 'noyau' };
    case 'choice':
      return { kind: 'choice', id: variable.pageId };
    case 'checkbox':
    case 'input':
      return { kind: 'field', id: variable.blockId };
    case 'objective':
      return { kind: 'objective', id: variable.objectiveId };
    default:
      return assertNever(variable, 'simulated variable');
  }
}

export function SimulationBar({
  variables,
  simulation,
  showAll,
  sources,
  onSimulation,
  onShowAll,
}: SimulationBarProps) {
  const showAllId = useId();

  const set = (key: string, value: string | boolean | null): void =>
    onSimulation({ ...simulation, [key]: value });

  return (
    <div
      data-testid={PAGE_EDITOR_IDS.simulation}
      className="space-y-2 rounded-md border-2 border-bleu-nuit bg-bleu-nuit-tint px-3 py-2"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="flex items-center gap-1.5 font-semibold text-bleu-nuit text-xs uppercase tracking-wide">
          <Eye aria-hidden className="size-4" />
          Simuler
        </span>

        {variables.map((variable) => {
          const label = pillOf(variableStandingOf(sources, null, variableOf(variable))).label;
          const value = simulatedValue(simulation, variable);
          if (variable.kind === 'noyau' || variable.kind === 'choice') {
            return (
              <label key={variable.key} className="flex items-center gap-2 text-gray-800 text-sm">
                <span>{variable.kind === 'noyau' ? 'Noyau retenu' : label}</span>
                <select
                  data-testid={PAGE_EDITOR_IDS.simulationControl}
                  data-variable={variable.key}
                  value={typeof value === 'string' ? value : ''}
                  onChange={(event) => {
                    const next = event.currentTarget.value;
                    set(variable.key, next === '' ? null : next);
                  }}
                  className="h-8 rounded-md border-2 border-gray-300 bg-white px-2 text-sm focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {variable.kind === 'noyau'
                    ? CONDITION_NOYAUX.map((code) => (
                        <option key={code} value={code}>
                          {noyauOptionLabel(code)}
                        </option>
                      ))
                    : variable.buttons.map((button) => (
                        <option key={button.id} value={button.id}>
                          « {button.label || 'Bouton sans libellé'} »
                        </option>
                      ))}
                  <option value="">
                    {variable.kind === 'noyau' ? 'Aucun (séquence sautée)' : 'Aucun choix'}
                  </option>
                </select>
              </label>
            );
          }
          const word =
            variable.kind === 'checkbox'
              ? 'cochée'
              : variable.kind === 'input'
                ? 'rempli'
                : 'réussi';
          return (
            <label key={variable.key} className="flex items-center gap-2 text-gray-800 text-sm">
              <input
                type="checkbox"
                data-testid={PAGE_EDITOR_IDS.simulationControl}
                data-variable={variable.key}
                checked={value === true}
                onChange={(event) => set(variable.key, event.currentTarget.checked)}
                className="size-4 accent-bleu-nuit"
              />
              <span>
                {label} <span className="text-gray-600">{word}</span>
              </span>
            </label>
          );
        })}

        <label
          htmlFor={showAllId}
          className="ml-auto flex items-center gap-2 font-semibold text-gray-800 text-sm"
        >
          <input
            id={showAllId}
            type="checkbox"
            data-testid={PAGE_EDITOR_IDS.simulationShowAll}
            checked={showAll}
            onChange={(event) => onShowAll(event.currentTarget.checked)}
            className="size-4 accent-bleu-nuit"
          />
          Montrer tous les blocs
        </label>
      </div>

      <p className={cn('text-xs leading-4', showAll ? 'text-gray-700' : 'text-bleu-nuit')}>
        {showAll
          ? 'Tous les blocs sont montrés, les conditionnels marqués. Fixez une valeur pour voir la page d’une personne.'
          : 'La page telle qu’une personne la verra avec ces valeurs : les blocs dont une condition est fausse ne sont pas là.'}
      </p>
    </div>
  );
}
